import api from './api';

export interface GrupoParticipante {
  id: string;
  jid: string;
  nome?: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export type GrupoTipoApi = 'UAZAPI' | 'META';

export interface Grupo {
  id: string;
  empresaId: string;
  instanciaUazapiId?: string | null;
  instanciaMetaId?: string | null;
  tipoApi?: GrupoTipoApi;
  /** Meta cria grupos de forma assíncrona: pendente -> ativo | falhou */
  statusCriacao?: 'ativo' | 'pendente' | 'falhou';
  erroCriacao?: string | null;
  suspenso?: boolean;
  /** false = o sistema ignora as mensagens do grupo (não cria ticket, contatos nem baixa mídias) */
  mostrarNaGestao?: boolean;
  jid: string;
  nome: string;
  descricao?: string | null;
  imagemUrl?: string | null;
  inviteLink?: string | null;
  isAnnounce: boolean;
  isLocked: boolean;
  isJoinApprovalRequired: boolean;
  memberAddMode?: string | null;
  isEphemeral: boolean;
  disappearingTimer?: number | null;
  isParent: boolean;
  linkedParentJid?: string | null;
  totalParticipantes: number;
  ativo: boolean;
  dataCriacao: string;
  dataAtualizacao?: string | null;
  participantes?: GrupoParticipante[];
  _count?: { participantes: number };
}

export interface AtualizarGrupoData {
  nome?: string;
  descricao?: string;
  imagem?: string;
  isAnnounce?: boolean;
  isLocked?: boolean;
  isJoinApprovalRequired?: boolean;
  memberAddMode?: 'admin_add' | 'all_member_add';
  ephemeralDuration?: '0' | 'off' | '1d' | '7d' | '90d';
}

/** Solicitação de entrada pendente (grupos Meta com aprovação de entrada) */
export interface SolicitacaoEntrada {
  joinRequestId: string;
  waId: string;
  criadoEm?: string | null;
}

/** Limite de participantes por grupo na Groups API da Meta */
export const META_LIMITE_PARTICIPANTES = 8;

export type AcaoParticipante = 'add' | 'remove' | 'promote' | 'demote' | 'approve' | 'reject';

export interface GrupoConvitePreview {
  jid: string;
  nome: string;
  descricao?: string | null;
  totalParticipantes: number;
  isLocked: boolean;
  isAnnounce: boolean;
  isJoinApprovalRequired: boolean;
}

export interface CriarGrupoUazapiData {
  instanciaUazapiId: string;
  nome: string;
  participantes: string[];
}

export interface CriarGrupoMetaData {
  instanciaMetaId: string;
  nome: string;
  descricao?: string;
  aprovacaoEntrada?: boolean;
}

export interface LimpezaMensagensResultado {
  conversasRemovidas: number;
  mensagensRemovidas: number;
  arquivosRemovidos: number;
}

export interface ExclusaoGrupoResultado extends LimpezaMensagensResultado {
  participantesRemovidos: number;
}

export interface LimpezaContatosResultado {
  contatosRemovidos: number;
  contatosPreservados: number;
  motivosPreservados?: {
    comConversaPropria: number;
    comAgendamento: number;
    naoSaoDeGrupo: number;
  } | null;
}

export const grupoService = {
  listar: (params?: {
    instanciaUazapiId?: string;
    instanciaMetaId?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    disponivelParaComunidade?: boolean;
  }) => api.get('/grupos', { params }),

  sincronizar: (instancia: { instanciaUazapiId?: string; instanciaMetaId?: string }, search?: string) =>
    api.post('/grupos/sincronizar', { ...instancia, search }),

  criar: (data: CriarGrupoUazapiData | CriarGrupoMetaData) =>
    api.post('/grupos', data),

  obterInfoConvite: (instanciaUazapiId: string, invitecode: string) =>
    api.post('/grupos/invite-info', { instanciaUazapiId, invitecode }),

  entrarViaConvite: (instanciaUazapiId: string, invitecode: string) =>
    api.post('/grupos/entrar', { instanciaUazapiId, invitecode }),

  obter: (id: string, refresh = false) =>
    api.get(`/grupos/${id}`, { params: refresh ? { refresh: 'true' } : undefined }),

  atualizar: (id: string, data: AtualizarGrupoData) =>
    api.put(`/grupos/${id}`, data),

  resetarLinkConvite: (id: string) =>
    api.post(`/grupos/${id}/invite-link/reset`),

  alterarParticipantes: (id: string, action: AcaoParticipante, participantes: string[]) =>
    api.post(`/grupos/${id}/participantes/acao`, { action, participantes }),

  sair: (id: string) =>
    api.delete(`/grupos/${id}`),

  atualizarVisibilidadeGestao: (id: string, mostrarNaGestao: boolean) =>
    api.patch(`/grupos/${id}/gestao`, { mostrarNaGestao }),

  limparMensagens: (id: string) =>
    api.post<LimpezaMensagensResultado>(`/grupos/${id}/limpar-mensagens`),

  /** Exclui o grupo no WhatsApp e no sistema; excluirTickets só vale se o parâmetro da empresa permitir */
  excluir: (id: string, excluirTickets = false) =>
    api.post<ExclusaoGrupoResultado>(`/grupos/${id}/excluir`, { excluirTickets }),

  limparContatos: (id: string) =>
    api.post<LimpezaContatosResultado>(`/grupos/${id}/limpar-contatos`),
};
